import { useMemo, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFlags } from "launchdarkly-react-client-sdk";

import CustomModal from "components/common/Modal";
import Select from "components/common/Select";
import Text from "components/common/Text";
import CustomDatePicker from "components/common/DatePicker";
import Button from "components/common/Button";
import { AlertType } from "components/common/Snackbar";
import SearchableAutoComplete from "components/common/AutoComplete";
import EditShiftModal from "components/Screens/Timesheet/EditShiftModal";
import { getEmployeeSchedule } from "services/CheckInCheckOut/CheckInAndBreaks";

import EmploymentInformationSchema from "validations/BambooHRTabs/EmploymentInformationSchema";

import {
  assignEmployeeToACustomer,
  assignEmployeeToACustomerV3,
  assignEmployeeToACustomerV4,
  assignEmployeeToACustomerV5,
  editEmployeeAssignment,
  editEmployeeAssignmentV2,
} from "services/Admin/Dashboard";
import { getCustomerEmployeeList } from "services/customer";
import { showSnackbar } from "store/actions/Utility";
import { GetEmployeeDetails } from "store/actions/GetEmployeeDetails";

import { EMPLOYEMENT_TYPE } from "constants";

import "./AssignToCustomerModal.scss";

function AssignToCustomerModal({
  open,
  closeModal,
  employeeId,
  showAssignToCustomerModalForEdit,
  initialEmploymentDetails,
  fetchEmployeeDetailsByIDV2,
  employmentStatus,
}) {
  const dispatch = useDispatch();
  const { anniversaryBasedTimeOff, checkInRevamped, temporaryReplacement, seperateEmployeeStartDateHr } = useFlags();
  const [assigningEmployee, setAssigningEmployee] = useState(false);
  const [currentView, setCurrentView] = useState("assign"); // 'assign' or 'editShift'
  const [shiftSchedule, setShiftSchedule] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Fetch employee schedule on mount
  useEffect(() => {
    const fetchSchedule = async () => {
      if (employeeId) {
        try {
          const scheduleData = await getEmployeeSchedule(employeeId);
          if (scheduleData?.schedule) {
            setShiftSchedule(scheduleData.schedule);
          } else {
            setShiftSchedule(null);
          }
        } catch (error) {
          // Error fetching schedule
          setShiftSchedule(null);
        }
      }
    };

    if (open && checkInRevamped) {
      fetchSchedule();
    }
  }, [employeeId, open, checkInRevamped]);

  const departmentsList = useMemo(
    () => JSON.parse(sessionStorage.getItem("dropdownData"))?.payload?.department || [],
    []
  );
  const customerList = useMemo(() => JSON.parse(sessionStorage.getItem("dropdownData"))?.payload?.customer || [], []);
  const companies = customerList.map(({ companyName, customerId }) => ({
    value: companyName,
    label: companyName,
    id: customerId,
  }));

  const departments = departmentsList.map(({ department }) => ({
    value: department,
    label: department,
  }));

  const employmentTypeOptions = useMemo(() => {
    const options = Array.isArray(EMPLOYEMENT_TYPE) ? EMPLOYEMENT_TYPE : [];
    if (!temporaryReplacement) {
      return options.filter((opt) => opt.value !== "Substitute");
    }
    const initialType = initialEmploymentDetails?.employmentType;
    if (initialType && initialType !== "Substitute") {
      return options.filter((opt) => opt.value !== "Substitute");
    }
    return options;
  }, [temporaryReplacement, initialEmploymentDetails?.employmentType]);

  // Transform substituteOf object to employeeId for Select component
  const formValues = useMemo(() => {
    if (!initialEmploymentDetails) return undefined;
    const { substituteOf, ...rest } = initialEmploymentDetails;
    const substituteOfValue =
      substituteOf && typeof substituteOf === "object" && substituteOf.employeeId
        ? substituteOf.employeeId
        : substituteOf;
    return { ...rest, substituteOf: substituteOfValue };
  }, [initialEmploymentDetails]);

  const methods = useForm({
    resolver: yupResolver(EmploymentInformationSchema),
    mode: "onChange",
    values: formValues,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isValid },
  } = methods;

  const selectedCompany = watch("company");
  const employmentType = watch("employmentType");
  const isSubstitute = temporaryReplacement && employmentType === "Substitute";

  const minSubstituteEndDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const substituteDisplayName = useMemo(() => {
    const substituteOf = initialEmploymentDetails?.substituteOf;
    if (!substituteOf || typeof substituteOf !== "object") return "";
    return [substituteOf.firstName, substituteOf.lastName].filter(Boolean).join(" ") || substituteOf.employeeId || "";
  }, [initialEmploymentDetails?.substituteOf]);

  // Fetch employees for selected company only when Employment type is Substitute
  useEffect(() => {
    if (!isSubstitute) {
      setEmployeeOptions([]);
      setValue("substituteOf", "");
      return;
    }

    if (!selectedCompany) {
      setEmployeeOptions([]);
      setValue("substituteOf", "");
      return;
    }

    const customerId = customerList.find((c) => c.companyName === selectedCompany)?.customerId;
    if (!customerId) return;

    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const response = await getCustomerEmployeeList(customerId);
        const rows = Array.isArray(response) ? response : (response?.rows ?? []);

        const options = rows
          .filter((emp) => emp.employeeId !== employeeId && emp.employeeStatus !== "Substitute" && !emp.hasSubstitute)
          .map((emp) => ({
            value: emp.employeeId,
            label: [emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.First_Name || emp.employeeId || "",
          }))
          .filter((opt) => opt.label);
        setEmployeeOptions(options);
      } catch {
        setEmployeeOptions([]);
      } finally {
        setLoadingEmployees(false);
      }
    };
    fetchEmployees();
  }, [isSubstitute, selectedCompany, employeeId, customerList, setValue]);

  const closeAssignToCustomerModal = () => {
    reset({});
    setCurrentView("assign");
    closeModal();
  };

  const handleBackToAssignCustomer = () => {
    setCurrentView("assign");
  };

  const handleEditShiftSuccess = async () => {
    // Refresh shift data from API after update
    try {
      const scheduleData = await getEmployeeSchedule(employeeId);
      if (scheduleData?.schedule) {
        setShiftSchedule(scheduleData.schedule);
      }
    } catch (error) {
      // Error fetching updated schedule
    }
  };

  const onSubmit = async (data) => {
    setAssigningEmployee(true);

    const {
      endDate: _endDate,
      substituteOf: _substituteOf,
      employeeHrStartDate: _employeeHrStartDate,
      ...restData
    } = data;

    const payload = {
      ...restData,
      ...(!showAssignToCustomerModalForEdit && {
        customerId: companies.find((company) => company.label === data?.company)?.id,
      }),
      employeeId,
      startDate: moment(data.dateOfJoining).format("YYYY-MM-DD"),
      ...(seperateEmployeeStartDateHr &&
        data.employeeHrStartDate && {
          startDateHR: moment(data.employeeHrStartDate).format("YYYY-MM-DD"),
        }),
      ...(temporaryReplacement &&
        data.employmentType === "Substitute" && {
          endDate: moment(data.endDate).format("YYYY-MM-DD"),
          substituteOf: data.substituteOf,
        }),
    };

    let res;
    if (showAssignToCustomerModalForEdit) {
      const assignmentId = initialEmploymentDetails?.assignmentId;
      res =
        temporaryReplacement && assignmentId
          ? await editEmployeeAssignmentV2(employeeId, assignmentId, payload)
          : await editEmployeeAssignment(employeeId, payload);
    } else {
      let assignFn;
      if (anniversaryBasedTimeOff) {
        if (seperateEmployeeStartDateHr) {
          assignFn = assignEmployeeToACustomerV5;
        } else if (temporaryReplacement) {
          assignFn = assignEmployeeToACustomerV4;
        } else {
          assignFn = assignEmployeeToACustomerV3;
        }
      } else {
        assignFn = assignEmployeeToACustomer;
      }
      res = await assignFn(employeeId, payload);
    }

    setAssigningEmployee(false);

    if (res?.payload?.error?.length > 0) {
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: res?.payload?.error?.[0]?.description || res?.payload?.error?.[0]?.msg || "An error occurred.",
          dispatch,
        })
      );
      closeAssignToCustomerModal();
      return;
    }

    fetchEmployeeDetailsByIDV2();
    dispatch(
      GetEmployeeDetails({
        customerID: companies.find((company) => company.label === data?.company)?.id,
        employeeID: employeeId,
        request: `employee/${employeeId}`,
      })
    );
    closeAssignToCustomerModal();
    dispatch(showSnackbar({ type: AlertType.success, message: "Assigned to Customer successfully", dispatch }));
  };

  return (
    <>
      {currentView === "assign" && (
        <CustomModal open={open} title="Assign to Customer" width="700px" onClose={closeAssignToCustomerModal}>
          <FormProvider {...methods}>
            <form
              className="mt-15"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit((data) => onSubmit(data, e))(e);
              }}
            >
              <div className="flex flex-col gap-1">
                <div className="flex gap-1">
                  <SearchableAutoComplete
                    name="company"
                    label="Company"
                    options={companies}
                    placeholder="Search by company name"
                    disabled={showAssignToCustomerModalForEdit}
                  />
                  <Select label="Department" name="department" placeholder="Select" options={departments} />
                </div>

                <div className="flex gap-1">
                  <Text label="Job title" name="jobTitle" placeholder="e.g. Client Associate" />
                  <CustomDatePicker
                    name="dateOfJoining"
                    disabled={
                      (showAssignToCustomerModalForEdit && employmentStatus !== "pending") ||
                      initialEmploymentDetails?.employmentType === "Substitute"
                    }
                    label="Date of joining"
                  />
                </div>

                <div className="flex gap-1">
                  <Select
                    label="Employment type"
                    name="employmentType"
                    placeholder="Select"
                    options={employmentTypeOptions}
                    disabled={initialEmploymentDetails?.employmentType === "Substitute"}
                  />
                  {seperateEmployeeStartDateHr && (
                    <CustomDatePicker name="employeeHrStartDate" label="Employee Start Date (HR)" />
                  )}
                </div>
                {isSubstitute && (
                  <>
                    {initialEmploymentDetails?.employmentType === "Substitute" ? (
                      <Text
                        label="Employee substituted"
                        name="substituteOfDisplay"
                        value={substituteDisplayName}
                        disabled
                      />
                    ) : (
                      <Select
                        label="Employee substituted"
                        name="substituteOf"
                        placeholder={loadingEmployees ? "Loading..." : "Select"}
                        options={employeeOptions}
                        disabled={!selectedCompany || loadingEmployees}
                      />
                    )}
                    <CustomDatePicker name="endDate" label="End date" minDate={minSubstituteEndDate} />
                  </>
                )}
              </div>
              <div className="modal-action-buttons">
                <Button onClick={closeAssignToCustomerModal} label="Cancel" variant="outlined" size="large" />
                <Button
                  isLoading={assigningEmployee}
                  isDisabled={!isValid}
                  type="submit"
                  label="Save"
                  variant="contained"
                  size="large"
                />
              </div>
            </form>
          </FormProvider>
        </CustomModal>
      )}

      {/* Edit Shift Modal */}
      {currentView === "editShift" && (
        <EditShiftModal
          open
          onClose={handleBackToAssignCustomer}
          onSuccess={handleEditShiftSuccess}
          assignToCustomer
          employeeId={employeeId}
          currentSchedule={shiftSchedule}
        />
      )}
    </>
  );
}

export default AssignToCustomerModal;
